import { createContext, useState, useCallback } from 'react';
import axiosInstance from '../../api/axiosInstance';

const InventoryEngineContext = createContext({
	loading: false,
	summary: { sourceName: '', partCount: 0, inventoryValue: 0 },
	setDatasource: () => {},
	onGridReady: () => {},
	gridApi: null,
	suppliersLoading: false,
	suppliersData: [],
	suppliersError: null,
	fetchSuppliers: () => {},
	supplier: {},
});

const buildParams = (request) => {
	const { supplierId, ...rest } = request;

	const removeNulls = (obj) => {
		if (Array.isArray(obj)) {
			return obj.filter((i) => i != null).map(removeNulls);
		} else if (obj && typeof obj === 'object') {
			return Object.fromEntries(
				Object.entries(obj)
					.filter(([, v]) => v != null)
					.map(([k, v]) => [k, removeNulls(v)])
			);
		}
		return obj;
	};

	const cleaned = removeNulls(rest);

	const filterModel = {
		...cleaned.filterModel,
		is_sci_stock: cleaned.filterModel?.is_sci_stock || { filter: '1' },
	};

	const sortModel =
		Array.isArray(cleaned.sortModel) && cleaned.sortModel.length
			? cleaned.sortModel
			: [{ colId: 'last_modified', sort: 'desc' }];

	return { ...cleaned, filterModel, sortModel, supplierId };
};

const formatRows = (products) => {
	if (!Array.isArray(products)) return [];
	return products.map((p) => {
		const meta = {};
		p.meta_data?.forEach((m) => (meta[m.key] = m.value));
		const qty = Number(p.stock_quantity) || 0;
		const cp = Number(meta.cost_price_usd) || 0;
		const rp = Number(meta.resale_price_usd) || 0;
		return {
			...p,
			...meta,
			isShow: meta.isShow === '1',
			is_franchise: meta.is_franchise === '1' ? 'Yes' : 'No',
			cost_price_usd: cp,
			resale_price_usd: rp,
			cost_subtotal_usd: qty * cp,
			resale_subtotal_usd: qty * rp,
		};
	});
};

const createServer = () => ({
	getData: async (request) => {
		try {
			const params = buildParams(request);
			const resp = await axiosInstance.post(
				'https://www.supremecomponents.com/wp-json/sci/v1/products',
				params,
				{ headers: { 'Content-Type': 'application/json' } }
			);
			const {
				products = [],
				totalCount,
				items_total_usd_value,
				sourcename,
			} = resp.data;
			return {
				success: true,
				rows: formatRows(products),
				lastRow: totalCount,
				summary: {
					sourceName: sourcename,
					partCount: totalCount,
					inventoryValue: items_total_usd_value,
				},
			};
		} catch {
			return { success: false, rows: [], lastRow: 0, summary: null };
		}
	},
});

const flattenSupplier = (supplier) => ({
	id: supplier.id,
	name: supplier.name,
	slug: supplier.slug,
	crm_source_name: supplier.meta?.crm_source_name || '',
	lead_time: supplier.meta?.lead_time || '',
	last_file_uploaded_date: supplier.meta?.last_file_uploaded_date || '',
	no_of_parts_uploaded: supplier.meta?.no_of_parts_uploaded || '',
	is_show: supplier.meta?.is_show,
});

export const InventoryEngineProvider = ({ children }) => {
	const [view, setView] = useState('full-view');
	const [gridApi, setGridApi] = useState(null);
	const [loading, setLoading] = useState(false);
	const [summary, setSummary] = useState({
		sourceName: '',
		partCount: 0,
		inventoryValue: 0,
	});
	const [suppliersLoading, setSuppliersLoading] = useState(false);
	const [suppliersData, setSuppliersData] = useState([]);
	const [suppliersError, setSuppliersError] = useState(null);
	const [supplier, setSupplier] = useState({});

	const onGridReady = useCallback((params) => {
		setGridApi(params.api);
	}, []);

	const setDatasource = useCallback(
		(supplierId) => {
			if (!gridApi) return;
			const server = createServer();
			gridApi.setServerSideDatasource({
				getRows: async (params) => {
					setLoading(true);
					try {
						params.request.supplierId = supplierId;
						const res = await server.getData(params.request);
						if (res.success) {
							setSummary(res.summary);
							params.success({ rowData: res.rows, rowCount: res.lastRow });
						} else {
							params.fail();
						}
					} finally {
						setLoading(false);
					}
				},
			});
		},
		[gridApi]
	);

	const fetchSuppliers = useCallback(() => {
		setSuppliersLoading(true);
		setSuppliersError(null);

		axiosInstance
			.get(
				'https://www.supremecomponents.com/wp-json/internal-api/v1/list-suppliers'
			)
			.then((response) => {
				const responseData = response.data?.suppliers || [];
				const flattenedData = responseData.map(flattenSupplier);
				setSuppliersData(flattenedData);
			})
			.catch((err) => {
				setSuppliersError(err);
			})
			.finally(() => {
				setSuppliersLoading(false);
			});
	}, []);

	return (
		<InventoryEngineContext.Provider
			value={{
				view,
				setView,
				loading,
				summary,
				setDatasource,
				onGridReady,
				gridApi,
				suppliersLoading,
				suppliersData,
				setSuppliersData,
				suppliersError,
				fetchSuppliers,
				supplier,
				setSupplier,
			}}
		>
			{children}
		</InventoryEngineContext.Provider>
	);
};

export default InventoryEngineContext;
